import React, { useState } from 'react'
import { LinearProgress, Stack, Table, TableBody, TableContainer, Typography } from '@mui/material'
import { Link } from 'react-router'
import { IssueTypeText } from '../../../components/entities/IssueType'
import { formatDateYYYYMMDD } from '../../../packages/utils/numberFormatters'
import { CompanyEventRow } from './CompanyEventRow'
import { NewIssueQuickButton } from './NewIssueQuickButton'
import { NewIssueQuickForm } from './NewIssueQuickForm'
import { calculateIssueStatus } from '../../../components/utils/calculateIssueStatus'
import IssueApi from '../../../api/IssueApi'
import SourceApi from '../../../api/SourceApi'
import { IssueEntity } from 'types/IssueEntity'
import { IssueType, PRInputEntity } from '@finance-lab/utils/types'
import { SourceType } from 'types/SourceType'
import { CompanyEntity } from 'types/CompanyEntity'
import { SourceEntity } from 'types/SourceEntity'

async function addPRToIssue(issue: IssueEntity, pr: Pick<PRInputEntity, 'id'>, sourceType: SourceType) {
    const sourceApi = new SourceApi()

    if (!issue || !pr || !sourceType) {
        console.error(
            `addPRToIssue: Missing ${issue ? '' : 'issue'} ${pr ? '' : 'pr'} ${sourceType ? '' : 'sourceType'}`,
        )
        return null
    }

    const newSource = {
        issueId: issue.id,
        sourceType: sourceType,
        prId: pr.id,
    }

    const status = calculateIssueStatus(issue)
    const issueFields = { statusStage: status.stage, statusComplete: status.status === 'complete' }
    const createdSource = await sourceApi.create(newSource, issueFields)
    return createdSource as SourceEntity
}

export const CompanyIssues = ({
    company,
    issues,
    sourceType,
    setSourceType,
    selectedPR,
    onArchivePR,
    onReloadIssues,
}: {
    company: Pick<CompanyEntity, 'id' | 'name'>
    issues: IssueEntity[] | null
    sourceType: SourceType | null
    setSourceType: (s: SourceType | null) => void
    selectedPR: Pick<PRInputEntity, 'id'>
    onArchivePR: (p: Pick<PRInputEntity, 'id'>) => Promise<void>
    onReloadIssues: () => Promise<void>
}) => {
    const [newIssueFormOpen, setNewIssueFormOpen] = useState(false)
    const issueApi = new IssueApi()

    const handleCreateIssue = async (issueData: { shareId: string; type: IssueType }) => {
        if (!company) return
        const result = await issueApi.create(issueData)

        if (result) {
            const sourceResult = await addPRToIssue(result, selectedPR, sourceType)
            void onReloadIssues()
            if (sourceResult) {
                if (onArchivePR) void onArchivePR(selectedPR)
            }
        }
    }

    if (!issues) return <LinearProgress />

    const enableQuickCreateButton = sourceType === 'Publication'

    return (
        <>
            <NewIssueQuickButton
                company={company}
                onClick={() => setNewIssueFormOpen(true)}
                sx={{ mb: 4 }}
                disabled={!enableQuickCreateButton}
            />
            <NewIssueQuickForm
                company={company}
                open={newIssueFormOpen}
                setOpen={setNewIssueFormOpen}
                handleCreateIssue={handleCreateIssue}
            />
            {issues.length === 0 && <Typography variant='body1'>Inga emissioner hittades</Typography>}
            {issues.length > 0 && (
                <TableContainer>
                    <Table>
                        <TableBody>
                            {issues.map((issue) => (
                                <CompanyEventRow
                                    key={issue.id}
                                    companyId={company.id}
                                    event={issue}
                                    eventType='issue'
                                    sourceType={sourceType}
                                    setSourceType={setSourceType}
                                    selectedPR={selectedPR}
                                    onCreateSource={addPRToIssue}
                                    onArchivePR={onArchivePR}
                                >
                                    <Stack>
                                        <Link to={`/admin/issues/${issue.id}`} target='_blank'>
                                            {issue.shareName} -{' '}
                                            {issue.subEndDate
                                                ? formatDateYYYYMMDD(issue.subEndDate)
                                                : formatDateYYYYMMDD(issue.publicationDate)}
                                        </Link>
                                        <i>
                                            <IssueTypeText issueType={issue.type} />
                                        </i>
                                    </Stack>
                                </CompanyEventRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </>
    )
}
